import React from 'react';

/**
 * ChunkErrorBoundary
 *
 * Detects React.lazy chunk-load errors (typical signature when a stale
 * service worker serves a missing/old bundle) and automatically:
 *   1. Unregisters all service workers
 *   2. Wipes all caches
 *   3. Hard-reloads the page once
 *
 * This is a self-healing safety net so users never see a blank dashboard
 * after a deploy that reshuffled chunk hashes.
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, recovering: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const msg = String(error && (error.message || error));
    const isChunkError =
      /Loading chunk \d+ failed/.test(msg) ||
      /Loading CSS chunk/.test(msg) ||
      /ChunkLoadError/.test(msg) ||
      /Failed to fetch dynamically imported module/.test(msg);

    // Only auto-recover once per page-load to avoid reload loops
    if (isChunkError && !sessionStorage.getItem('ic_chunk_recovered')) {
      sessionStorage.setItem('ic_chunk_recovered', '1');
      this.recover();
    } else {
      console.error('App crashed:', error, info);
    }
  }

  async recover() {
    this.setState({ recovering: true });
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) {
      console.warn('Cache cleanup error (continuing to reload):', e);
    }
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      const errorMsg = String(this.state.error && (this.state.error.message || this.state.error)).slice(0, 600);
      return (
        <div
          style={{
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            textAlign: 'center',
            background: 'linear-gradient(135deg,#1a7a5a 0%,#e8b85f 100%)',
            color: '#fff',
            fontFamily: 'system-ui,-apple-system,sans-serif',
          }}
        >
          <div style={{ maxWidth: 600 }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 22 }}>
              {this.state.recovering ? 'Updating IC FunFlow…' : 'Something went wrong'}
            </h2>
            <p style={{ opacity: 0.9, marginBottom: 14, lineHeight: 1.5 }}>
              {this.state.recovering
                ? 'We are clearing stale data and reloading. Hang on a second…'
                : 'Tap "Reload App" below. If the issue keeps happening, please copy the error and share it with support.'}
            </p>
            {!this.state.recovering && (
              <>
                <div
                  style={{
                    background: 'rgba(0,0,0,0.35)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 8,
                    padding: 10,
                    fontSize: 12,
                    fontFamily: 'monospace',
                    textAlign: 'left',
                    margin: '12px auto 18px',
                    maxWidth: 560,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                  }}
                  data-testid="error-details"
                >
                  {errorMsg}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      sessionStorage.removeItem('ic_chunk_recovered');
                      this.recover();
                    }}
                    style={{
                      background: '#fff',
                      color: '#1a7a5a',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: 999,
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Reload App
                  </button>
                  <button
                    onClick={() => { try { navigator.clipboard.writeText(errorMsg); } catch (_) {} }}
                    style={{
                      background: 'transparent',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.4)',
                      padding: '12px 24px',
                      borderRadius: 999,
                      fontSize: 14,
                      cursor: 'pointer',
                    }}
                  >
                    Copy Error
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
